import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Search, HelpCircle, MessageSquare, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Seo } from "@/components/Seo";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { TopBar, Footer } from "./Help";

const QA = [
  { cat: "pricing", q: "Is Sabzy AI really free?", a: "Every new account gets a 7-day Pro Max free trial — all models, web search, multimodal. After 7 days you stay on the free tier (Gemini 3 Flash, GPT-5.4 Mini, GPT-5 Nano, Claude Haiku 4.5) unless you redeem a promo code or upgrade." },
  { cat: "pricing", q: "Do I need a credit card to sign up?", a: "No. We don't ask for a card during the trial. We're passwordless — we just need your email so we can send sign-in codes." },
  { cat: "models", q: "Which models do you use?", a: "19 frontier models from three labs: OpenAI (ChatGPT Astra / GPT-6, GPT-5.6 Sol, Terra & Luna, GPT-5.5, GPT-5.4, GPT-5.4 Mini, GPT-5 Nano, GPT-4o), Anthropic (Claude Opus 5, Opus 4.8, Opus 4.7, Sonnet 5, Sonnet 4.6, Haiku 4.5) and Google (Gemini 3.8 Flash, 3.5 Flash, 3.1 Pro, 3 Flash). Pick any model from the picker in the composer." },
  { cat: "models", q: "What is 'Racing' mode?", a: "Choose 'Racing Mode' in the model picker (Pro Max). The same prompt goes to GPT-5.4, Claude Sonnet 4.6 and Gemini 3.1 Pro simultaneously; the first quality response wins — usually inside 2–3 seconds — and you see per-model timing inline." },
  { cat: "features", q: "Does Sabzy do live web search?", a: "Yes — powered by Tavily. Toggle the globe icon in the composer or just ask a recent/factual question and Sabzy will enable it automatically. Sources are cited inline." },
  { cat: "features", q: "Can I use Sabzy on my phone?", a: "Yes. sabzy.in works as a progressive web app — add it to your home screen for an app-like experience. Voice input is on the roadmap." },
  { cat: "privacy", q: "Are my chats private?", a: "Yes. Your conversations and memory are linked to your email and never sold, shared with advertisers, or used to train public models. You can delete your account anytime by writing to support@sabzy.in." },
  { cat: "privacy", q: "Why did my verification email land in spam?", a: "Most providers will whitelist us once you mark one Sabzy email as 'Not spam'. We're a transactional sender — no marketing — so your future codes will land in the inbox. We're also rolling out a verified noreply@sabzy.in sender soon." },
  { cat: "account", q: "How do I delete my data?", a: "Email support@sabzy.in from the address you signed up with and we'll purge your account, conversations and memory within 24 hours." },
];

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "pricing", label: "Pricing" },
  { id: "models", label: "Models" },
  { id: "features", label: "Features" },
  { id: "privacy", label: "Privacy" },
  { id: "account", label: "Account" },
];

export default function Faq() {
  const [open, setOpen] = useState(0);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return QA.filter((item) => {
      const catOk = cat === "all" || item.cat === cat;
      const searchOk =
        !needle ||
        item.q.toLowerCase().includes(needle) ||
        item.a.toLowerCase().includes(needle);
      return catOk && searchOk;
    });
  }, [cat, q]);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: QA.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground grain">
      <Seo
        title="Sabzy AI FAQ — Pricing, models, privacy, racing & web search"
        description="Answers to the most common Sabzy AI questions: is it free? Which GPT, Claude and Gemini models? How does racing mode work? Is my data private? 10+ answers."
        path="/faq"
        jsonLd={faqJsonLd}
      />

      {/* BG */}
      <div className="absolute inset-0 -z-10 grid-overlay opacity-15" />
      <div className="absolute left-1/2 top-0 -z-10 h-[460px] w-[820px] -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />

      <TopBar />

      {/* HERO */}
      <section className="mx-auto max-w-3xl px-5 pt-14 pb-8 sm:px-8 sm:pt-20 sm:pb-12">
        <div className="label-mono text-primary">[ 02 · FAQ ]</div>
        <h1
          className="mt-3 text-4xl font-light tracking-[-0.035em] sm:text-5xl lg:text-6xl"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Frequently <span className="copper-text font-medium">asked</span>.
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
          Quick, scannable answers. Filter by category or search.
        </p>

        {/* Search */}
        <div className="relative mt-7" data-testid="faq-search">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search questions…"
            className="h-12 rounded-2xl border-border/70 bg-card/40 pl-11 pr-4 text-[15px] backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-primary/30"
            data-testid="faq-search-input"
          />
        </div>

        {/* Category chips */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="faq-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`rounded-full border px-3.5 py-1.5 text-[12px] font-medium transition ${
                cat === c.id
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border/60 bg-card/30 text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
              data-testid={`faq-cat-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* QUESTIONS */}
      <section className="mx-auto max-w-3xl px-5 pb-12 sm:px-8">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border/60 bg-card/20 p-10 text-center" data-testid="faq-empty">
            <HelpCircle className="mx-auto h-6 w-6 text-primary" />
            <h3 className="mt-3 text-base font-semibold">No questions match your search</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Try a different keyword or{" "}
              <Link to="/contact" className="text-foreground underline-offset-2 hover:underline">ask us directly</Link>.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {filtered.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={`${item.cat}-${i}`}
                  className={`overflow-hidden rounded-2xl border bg-card/40 backdrop-blur-sm transition ${
                    isOpen ? "border-primary/40 shadow-[0_10px_40px_-12px_rgba(196,122,58,0.25)]" : "border-border/60"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-[15px] font-medium text-foreground transition hover:bg-secondary/30"
                    data-testid={`faq-item-${i}`}
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-center gap-3">
                      <span className="rounded-md bg-secondary/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {item.cat}
                      </span>
                      <span>{item.q}</span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180 text-primary" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="border-t border-border/40 px-5 pb-5 pt-4 text-[14px] leading-relaxed text-muted-foreground"
                      data-testid={`faq-answer-${i}`}
                    >
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl px-5 pb-20 sm:px-8">
        <Link
          to="/contact"
          className="group flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-gradient-to-br from-card/60 to-card/30 p-5 transition hover:border-primary/50"
          data-testid="faq-cta-contact"
        >
          <div>
            <div className="mb-1 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <span className="text-[15px] font-semibold tracking-tight">Still stuck?</span>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Drop us a line — we reply in hours, not days.
            </p>
          </div>
          <div className="flex items-center gap-1 text-[12px] font-medium text-primary">
            Contact us
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </div>
        </Link>
      </section>

      <Footer />
      <ChatSupportWidget />
    </div>
  );
}
